import { NetworkStatus } from "@apollo/client";
import { ComponentProps, useState } from "react";
import { LayoutGridIcon, TableIcon } from "lucide-react";

import { TableColumnType } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { CardGrid } from "@/components/organisms/DataView/DataViewCardGrid";
import { Table } from "@/components/organisms/Table";
import { TableDialog } from "@/components/organisms/Table/TableDialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type ViewMode = "table" | "card";

type DataViewProps = {
  allowedViewModes?: ViewMode[];
  columns?: TableColumnType[];
  isLoading?: boolean;
  loadNext?: () => void;
  /** May return a promise — the empty state's button spins until it settles. */
  onAddNew?: () => void | Promise<unknown>;
  rows: any[];
  totalCount: number;
  unlockedColumnsStartIndex?: number;
  onCheckedRowsButtonsProps?: ComponentProps<typeof TableDialog>["buttons"];
  onCheckedRowsExtraActions?: ComponentProps<
    typeof TableDialog
  >["extraActions"];
  networkStatus?: NetworkStatus;
  onRowClick?: (row: any) => void;
  viewMode?: ViewMode;
  hideCheckboxes?: boolean;
  hideOuterBorders?: boolean;
  /** What card mode shows in the middle of the view when there are no rows. */
  emptyState?: ComponentProps<typeof CardGrid>["emptyState"];
  /** What card mode draws a card for while its rows load; given where cards are offered. */
  placeholderRow?: ComponentProps<typeof CardGrid>["placeholderRow"];
  /** How tall its cards are; ``md`` unless a kind is said in less. */
  cardSize?: ComponentProps<typeof CardGrid>["size"];
};

export function DataView({
  allowedViewModes = ["table", "card"],
  viewMode: externalViewMode,
  onAddNew,
  emptyState,
  placeholderRow,
  cardSize,
  ...props
}: DataViewProps) {
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("table");
  const isExternallyControlled = externalViewMode !== undefined;

  // Clamp viewMode to allowed options
  const rawViewMode = externalViewMode ?? internalViewMode;
  const viewMode = allowedViewModes.includes(rawViewMode)
    ? rawViewMode
    : allowedViewModes[0];

  return (
    <div className="w-full h-full flex flex-col relative">
      {/* View mode toggle - only shown when not externally controlled and multiple modes available */}
      {!isExternallyControlled && allowedViewModes.length > 1 && (
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={viewMode}
          onValueChange={(mode) =>
            mode && setInternalViewMode(mode as ViewMode)
          }
          className="absolute top-2 right-3 z-10 bg-card"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <ToggleGroupItem value="table" aria-label="Table view">
                <Icon ReactIcon={TableIcon} />
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent side="bottom">Table view</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <ToggleGroupItem value="card" aria-label="Card view">
                <Icon ReactIcon={LayoutGridIcon} />
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent side="bottom">Card view</TooltipContent>
          </Tooltip>
        </ToggleGroup>
      )}

      {/* Content area */}
      <div className="flex-1 min-h-0">
        {viewMode === "table" ? (
          <Table {...props} onAddNew={onAddNew} />
        ) : (
          <CardGrid
            {...props}
            onAddNew={onAddNew}
            emptyState={emptyState}
            placeholderRow={placeholderRow}
            size={cardSize}
          />
        )}
      </div>
    </div>
  );
}
